import { useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import Icon1 from "@assets/icons/Icon";
import { paymentsApi } from "@services/api/payments";
import { showNotification } from "context/notification.service";
import { useDataStore } from "@store/user_org";
import type { Ordre } from "types/ordre";

const TYPE_OPTIONS = [
  { value: "acompte", label: "Acompte" },
  { value: "solde", label: "Solde (paiement final)" },
];

interface Props {
  ordre: Ordre;
  defaultType: "acompte" | "solde";
  onClose: () => void;
  onSuccess: () => void;
}

export default function PaymentRequestModal({ ordre, defaultType, onClose, onSuccess }: Props) {
  const orgData = useDataStore((s) => s.orgData);

  const [type, setType] = useState<string>(defaultType);
  const [amount, setAmount] = useState(ordre.amount ? String(ordre.amount) : "");
  const [clientName, setClientName] = useState(
    ordre.contacts?.[0]?.nom || ""
  );
  const [clientFirstName, setClientFirstName] = useState(
    ordre.contacts?.[0]?.prenom || ""
  );
  const [clientEmail, setClientEmail] = useState(
    ordre.contacts?.[0]?.email || ""
  );
  const [clientPhone, setClientPhone] = useState(
    ordre.contacts?.[0]?.phone || ""
  );
  const [loading, setLoading] = useState(false);

  const bridgeConnected = orgData?.bridge?.connected;

  const handleSubmit = async () => {
    if (!amount || !clientEmail || !clientName) {
      showNotification({ type: "error", message: "Veuillez remplir tous les champs obligatoires" });
      return;
    }

    setLoading(true);
    try {
      await paymentsApi.create({
        ordre_id: ordre.id,
        type: type as "acompte" | "solde",
        amount: parseFloat(amount),
        reglement_infos: {
          email: clientEmail,
          name: clientName,
          first_name: clientFirstName || undefined,
          phone: clientPhone || undefined,
        },
      });
      showNotification({ type: "success", message: "Demande de paiement envoyée" });
      onSuccess();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Erreur lors de la création du paiement";
      showNotification({ type: "error", message: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      title="Demander un paiement"
      startIcon={<Icon1 name="payments" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">
        {!bridgeConnected && (
          <div
            className="flex flex-row items-center gap-2 p-3 rounded-lg"
            style={{ background: "#FEF9C3", border: "1px solid #FDE68A" }}
          >
            <span
              className="text-h3"
              style={{ fontFamily: "Poppins", color: "#92400E" }}
            >
              Bridge n'est pas configuré. Allez dans Paramètres &gt; Paiements pour configurer votre IBAN.
            </span>
          </div>
        )}

        <DropDown
          label="Type de paiement"
          options={TYPE_OPTIONS}
          value={type}
          onSelect={(v) => setType(String(v))}
        />

        <TextField
          label="Montant (€)"
          type="number"
          value={amount}
          onChange={setAmount}
        />

        <TextField
          label="Nom du client *"
          value={clientName}
          onChange={setClientName}
        />

        <TextField
          label="Prénom du client"
          value={clientFirstName}
          onChange={setClientFirstName}
        />

        <TextField
          label="Email du client *"
          value={clientEmail}
          onChange={setClientEmail}
        />

        <TextField
          label="Téléphone du client"
          value={clientPhone}
          onChange={setClientPhone}
        />
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={loading ? "Envoi..." : "Envoyer la demande"}
          onClick={handleSubmit}
          disabled={loading || !bridgeConnected}
        />
      </div>
    </BaseModal>
  );
}
